import { encrypt } from '@/common/securityUtils';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import useGetEditorsPick from '@/features/recommendation-management/editors-pick/hooks/useGetEditorsPick';
import { EditorsPickTable } from '@/features/recommendation-management/editors-pick/table';
import { Translation } from '@/hoslog-intl';
import { SortIcon } from '@/icons/sort-icon';
import { createFileRoute, SearchSchemaInput } from '@tanstack/react-router';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { z } from 'zod';

const defaultLocationValue = encrypt('1');
const locationIdParser = parseAsString.withDefault(defaultLocationValue);
const pageParser = parseAsInteger.withDefault(0);
const sortItemSchema = z.object({
  id: z.enum(['storeNameEng', 'description', 'tags', 'updatedDate', 'createdDate']),
  desc: z.boolean(),
});

const EditorsPickSearchSchema = z.object({
  sort: z
    .preprocess((val) => {
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return undefined;
        }
      }
      return val;
    }, z.array(sortItemSchema))
    .optional(),

  requestedDate: z
    .string()

    .optional(),

  q: z
    .any()
    .transform((val) => (val == null ? undefined : String(val)))
    .optional(),
  locationId: z.any().transform((val) => (val == null ? undefined : String(val))),
  page: z.coerce.number().positive().catch(0),
  fetch: z.coerce.number().positive().max(100).catch(5),
});

export const Route = createFileRoute('/(app)/recommendation-management/editors-pick/')({
  loader: () => {
    return {
      crumb: <CrumbTitle id="recommendationManagement.editorsPick.crumb" icon={SortIcon} />,
      title: <Translation id="recommendationManagement.editorsPick.title" />,
    };
  },
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => EditorsPickSearchSchema.parse(search),
});
function RouteComponent() {
  const [locationId, setLocationId] = useQueryState('locationId', locationIdParser);
  const [, setPage] = useQueryState('page', pageParser);
  const handleLocationChange = (value: string) => {
    void setLocationId(value);
    void setPage(0);
  };
  const { editorsPick, pagination, isLoading } = useGetEditorsPick(locationId);

  return (
    <EditorsPickTable
      data={editorsPick}
      pagination={pagination}
      loading={isLoading}
      location={locationId}
      onLocationChange={handleLocationChange}
    />
  );
}
